import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Loader2 } from "lucide-react";
import { aiMatterApi } from "../../services/api";

interface MatterMatch {
  matterId: string;
  matterTitle: string;
  matterNumber: string;
  clientName: string;
  confidence: number;
  reason: string;
}

interface AiMatterSuggestionProps {
  content: string;
  contentType: string;
  onSelect: (matterId: string) => void;
  clientId?: string;
}

const AiMatterSuggestion: React.FC<AiMatterSuggestionProps> = ({
  content,
  contentType,
  onSelect,
  clientId,
}) => {
  const [match, setMatch] = useState<MatterMatch | null>(null);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const lastContentRef = useRef("");

  useEffect(() => {
    // Reset dismissed state when content changes meaningfully
    if (content !== lastContentRef.current) {
      setDismissed(false);
    }

    // Don't match on short content
    if (!content || content.trim().length < 15) {
      setMatch(null);
      return;
    }

    // Debounce — wait for user to stop typing
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      // Skip if content hasn't changed
      if (content === lastContentRef.current) return;
      lastContentRef.current = content;

      setLoading(true);
      try {
        const res = await aiMatterApi.match({
          content,
          contentType,
          clientId,
        });
        const matches: MatterMatch[] = res.data.matches || [];
        setMatch(matches.length > 0 ? matches[0] : null);
      } catch {
        setMatch(null);
      } finally {
        setLoading(false);
      }
    }, 1200);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [content, contentType, clientId]);

  if (dismissed || (!match && !loading)) return null;

  return (
    <AnimatePresence>
      {loading && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          className="flex items-center gap-2 mt-1.5 px-3 py-1.5 text-xs text-slate-400"
        >
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>Analyzing for matter match...</span>
        </motion.div>
      )}

      {!loading && match && (
        <motion.div
          initial={{ opacity: 0, y: -6, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.97 }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
          className="flex items-center gap-2 mt-1.5"
        >
          <button
            type="button"
            onClick={() => {
              onSelect(match.matterId);
              setDismissed(true);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-medium text-emerald-800 hover:bg-emerald-100 hover:border-emerald-300 transition-colors group"
          >
            <Sparkles className="h-3 w-3 text-emerald-500" />
            <span>AI suggests:</span>
            <span className="font-semibold">{match.matterTitle}</span>
            <span className="text-emerald-500/70 ml-0.5">
              {Math.round(match.confidence * 100)}%
            </span>
          </button>

          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="p-0.5 text-slate-300 hover:text-slate-500 transition-colors"
            title="Dismiss suggestion"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AiMatterSuggestion;
